import { wtUserApi } from 'api/clients';

import type { GenericResponse } from 'commons/modules/generic-response';

import {
  ApiResponse,
  adaptApiToGenericResponse,
  adaptErrorToGenericResponse,
} from '../shared';

import type { UserAccessConfigDto } from './dto';
import {
  mapUserAccessConfigDtoToModel,
  mapUserAccessConfigModelToDto,
} from './mappers';
import type { UserAccessConfig } from './models';

const USER_ACCESS_CONFIGS_PATH = '/management/user/access/configs';

const mapUserAccessResponseToApiResponse = (
  response: ApiResponse<UserAccessConfigDto>,
): ApiResponse<UserAccessConfig> => ({
  ...response,
  data: mapUserAccessConfigDtoToModel(response.data),
});

export const getUserAccessConfigByEmail = async (
  email: string,
): Promise<GenericResponse<ApiResponse<UserAccessConfig>>> => {
  try {
    const response = await wtUserApi.get<ApiResponse<UserAccessConfigDto>>(
      `${USER_ACCESS_CONFIGS_PATH}/${email}`,
    );

    const mappedResponse = mapUserAccessResponseToApiResponse(response.data);

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al obtener información',
    );
  }
};

export const saveUserAccessConfigByEmail = async (
  email: string,
  body: UserAccessConfig,
): Promise<GenericResponse<ApiResponse<UserAccessConfig>>> => {
  try {
    const response = await wtUserApi.post<ApiResponse<UserAccessConfigDto>>(
      `${USER_ACCESS_CONFIGS_PATH}/${email}`,
      mapUserAccessConfigModelToDto(body),
    );

    const mappedResponse = mapUserAccessResponseToApiResponse(response.data);

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(error, 'Ocurrió un error al guardar');
  }
};

export const updateUserAccessConfigByEmail = async (
  email: string,
  body: UserAccessConfig,
): Promise<GenericResponse<ApiResponse<UserAccessConfig>>> => {
  try {
    const response = await wtUserApi.put<ApiResponse<UserAccessConfigDto>>(
      `${USER_ACCESS_CONFIGS_PATH}/${email}`,
      mapUserAccessConfigModelToDto(body),
    );

    const mappedResponse = mapUserAccessResponseToApiResponse(response.data);

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(error, 'Ocurrió un error al actualizar');
  }
};

export const deleteUserAccessConfigByEmail = async (
  email: string,
): Promise<GenericResponse<void>> => {
  try {
    const response = await wtUserApi.delete(
      `${USER_ACCESS_CONFIGS_PATH}/${email}`,
    );

    const result = adaptApiToGenericResponse(response.data, response.status);

    return {
      ...result,
      data: null,
    };
  } catch (error: any) {
    return adaptErrorToGenericResponse(error, 'Ocurrió un error al eliminar');
  }
};
